import React from "react";

import API from "@/helpers/API";
import Cookie from "@/helpers/Cookie";
import { AuthContext } from "@/providers/AuthProvider";
import { CampaignContext } from "@/providers/AuthProvider/campaignInfoProvider";
import { LaboratoryContext } from "@/providers/AuthProvider/labInfoProvider";
import { PharmacyContext } from "@/providers/AuthProvider/pharmacyInfoProvider";
import { EUserRole } from "@/providers/AuthProvider/interfaces";
import useDidUpdate from "@/hooks/useDidUpdate";

import {
  ELocale,
  ELocaleActionType,
  TFormConfig,
  TGetTranslationCallback,
  TLocaleContext,
  TLocaleProps,
  TLocaleTranslation,
  TTranslation,
  TTranslationValue,
  _TLocaleState,
} from "./interfaces";
import reducer from "./reducer";
import {
  getTranslationsForLaboratory,
  getTranslationsForPharmacy,
} from "@/helpers/API/requests/translations";
import { TranslationsAPIResponse } from "@/helpers/API/requests/interfaces/translations";
import {
  dispatchLocaleChange,
  getCurrentLocale,
  localeCookieName,
} from "@/helpers/locale";

export const g_listLocales = Object.values(ELocale);

// function to detect the language of the page (used for languagechange / MutationObserver)
const detectLanguage = (): ELocale => {
  const pageLanguage = document.documentElement.getAttribute("lang");
  const navigatorLanguage = navigator.language || navigator.languages[0];

  if (g_listLocales.findIndex((locale) => locale === pageLanguage) !== -1) {
    // if the detected language is "en-US", force the selection of en-GB
    if (pageLanguage === "en-US") {
      return ELocale.enGb;
    }
    return g_listLocales[
      g_listLocales.findIndex((locale) => locale === pageLanguage)
    ];
  } else if (
    g_listLocales.findIndex(
      (locale) => locale.substring(0, 2) === pageLanguage,
    ) !== -1
  ) {
    // if the detected language is "en", force the selection of en-GB
    if (pageLanguage === "en") {
      return ELocale.enGb;
    } else {
      return g_listLocales[
        g_listLocales.findIndex(
          (locale) => locale.substring(0, 2) === pageLanguage,
        )
      ];
    }
  } else if (
    g_listLocales.findIndex((locale) => locale === navigatorLanguage) !== -1
  ) {
    // if the browser language is "en-US", force the selection of en-GB
    if (navigatorLanguage === "en-US") {
      return ELocale.enGb;
    }
    return g_listLocales[
      g_listLocales.findIndex((locale) => locale === navigatorLanguage)
    ];
  } else if (
    g_listLocales.findIndex(
      (locale) => locale.substring(0, 2) === navigatorLanguage,
    ) !== -1
  ) {
    // if the language of the browser is "en", force the selection of en-GB
    if (navigatorLanguage.substring(0, 2) === "en") {
      return ELocale.enGb;
    } else {
      return g_listLocales[
        g_listLocales.findIndex(
          (locale) => locale.substring(0, 2) === navigatorLanguage,
        )
      ];
    }
  } else {
    return ELocale.frFr;
  }
};

const defaultLocale = getCurrentLocale();

export const defaultState: _TLocaleState = {
  nextLocale: defaultLocale,
  locale: defaultLocale,
};

export const LocaleContext = React.createContext<TLocaleContext>({
  ...defaultState,

  setLocale: () => {},

  getTranslation: () => "",

  getFormConfig: () => ({}),
});

LocaleContext.displayName = "Locale";

export const LocaleConsumer = LocaleContext.Consumer;

/**
 *  The global translation's object must use locale as key and translation's object as value.
 *
 *  The translation's object keys are translation keys and his values must be an array storing
 *  singular and plural formatted string
 *  or translation's object since the provider allowing deep recursive key.
 *
 *  Recursive key value must be separated by '.'.
 *  Finally, there are 2 way to inject variable into formatted translation.
 *  by using an object and js synthax ${variable} or using an array with printf synthax (%s, %d).
 *  Exemple:
 *      {
 *          [ELocale.frFr]: {
 *              'hello': ["Bonjour ${name} !"],
 *              'hello2': ["Bonjour %s !"],
 *              form: {
 *                  name: ['Nom du formulaire'] // Deep recursive key
 *              },
 *              cat: ['chat', 'chats'] // With plural value
 *          }
 *      }
 *
 * getTranslation('hello', { params: { name: "Toto" } }); // Expected: "Bonjour Toto !"
 * getTranslation('hello2', { params: ['Toto'] }); // Expected: "Bonjour Toto !"
 * getTranslation('form.name'); // Expected: "Nom du formulaire"
 * getTranslation('cat'); // Expected: "chat"
 * getTranslation('cat', { plural: true }); // Expected: "chats"
 *
 */

const LocaleProvider: React.FC<TLocaleProps> = ({
  translations: pTranslations = {},
  getTranslationFromRemote,
  renderKeyIfNotFound = process.env.NODE_ENV === "development",
  renderLoading = null,
  injectLocaleIntoAPIOptions,
  children,
}) => {
  // Contextes pour déterminer la typologie (campaign = labo, pharmacy = pharmacie)
  const { user, laboratoryIdentifier: authLabIdentifier } =
    React.useContext(AuthContext);
  const {
    isLaboratory,
    token: campaignToken,
    campaignLoading,
    labIdentifier: campaignLabIdentifier,
  } = React.useContext(CampaignContext);
  const { token: labToken, labLoading } = React.useContext(LaboratoryContext);
  const { pharmacy } = React.useContext(PharmacyContext);

  const labIdentifier = campaignLabIdentifier ?? authLabIdentifier;

  const isLabUser = Boolean(
    user?.roles?.includes(EUserRole.LAB) ||
      user?.roles?.includes(EUserRole.ANIMATOR),
  );

  const translations = React.useRef<TLocaleTranslation>(pTranslations);

  // Cache for translations fetched from the backend
  const [remoteTranslations, setRemoteTranslations] = React.useState<
    {
      [locale in ELocale]?: TranslationsAPIResponse;
    }
  >({});

  // Management of loading by locale
  const [loadingLocales, setLoadingLocales] = React.useState<Set<ELocale>>(
    new Set(),
  );

  // Management of errors by locale
  const [errorLocales, setErrorLocales] = React.useState<Set<ELocale>>(
    new Set(),
  );

  // Debug logs for development only
  React.useEffect(() => {
    if (process.env.REACT_APP_ENV !== "production") {
      console.log("loadingLocales:", Array.from(loadingLocales));
    }
  }, [loadingLocales]);

  React.useEffect(() => {
    if (process.env.REACT_APP_ENV !== "production") {
      console.log("errorLocales:", Array.from(errorLocales));
    }
  }, [errorLocales]);

  const [{ locale, nextLocale, loading }, dispatch] = React.useReducer(
    reducer,
    {
      ...defaultState,
      loading: typeof getTranslationFromRemote === "function",
    },
  );

  // Détermine la typologie : labo (campaign) vs pharmacie. Ne jamais appeler la route pharmacy si on est labo.
  const processType = React.useMemo<"campaign" | "pharmacy" | null>(() => {
    if (isLaboratory || campaignToken || labToken || isLabUser)
      return "campaign";
    if (pharmacy) return "pharmacy";
    if (campaignLoading || labLoading) return null;
    return null;
  }, [
    isLaboratory,
    campaignToken,
    labToken,
    isLabUser,
    pharmacy,
    campaignLoading,
    labLoading,
  ]);

  const availableToken = React.useMemo(() => {
    return campaignToken || labToken || null;
  }, [campaignToken, labToken]);

  const fetchTranslations = React.useCallback(
    async (
      locale: ELocale,
      retryCount = 0,
    ): Promise<TranslationsAPIResponse | null> => {
      // Ne pas appeler l'API tant que le type (pharmacy vs laboratory) n'est pas déterminé
      if (processType === null) return null;
      // En mode labo, attendre la fin du chargement campaign/lab ; le token peut venir du cookie (user connecté)
      if (processType === "campaign" && (campaignLoading || labLoading)) {
        return null;
      }

      // Déterminer la locale à utiliser pour le fetch (en-US -> en-GB)
      const fetchLocale = locale === ELocale.enUs ? ELocale.enGb : locale;

      // Vérifier si déjà en cache (utiliser la locale originale pour le cache)
      if (remoteTranslations[locale]) {
        return remoteTranslations[locale] || null;
      }

      // Marquer comme en cours de chargement
      setLoadingLocales((prev) => new Set(prev).add(locale));

      // Retirer de la liste des erreurs si présent
      setErrorLocales((prev) => {
        const newSet = new Set(prev);
        newSet.delete(locale);
        return newSet;
      });

      try {
        let fetchedTranslations: TranslationsAPIResponse;

        // Utiliser la bonne fonction selon la typologie avec la locale de fetch
        if (processType === "campaign") {
          fetchedTranslations = await getTranslationsForLaboratory(fetchLocale);
        } else {
          fetchedTranslations = await getTranslationsForPharmacy(fetchLocale);
        }

        // Stocker en cache avec la locale originale (en-US garde son cache séparé)
        setRemoteTranslations((prev) => ({
          ...prev,
          [locale]: fetchedTranslations,
        }));

        // Retirer du loading
        setLoadingLocales((prev) => {
          const newSet = new Set(prev);
          newSet.delete(locale);
          return newSet;
        });

        return fetchedTranslations;
      } catch (error) {
        console.error(
          `Erreur lors du fetch des traductions ${locale} (via ${fetchLocale}) pour ${processType} (tentative ${
            retryCount + 1
          }/3):`,
          error,
        );

        // Retry jusqu'à 3 fois
        if (retryCount < 2) {
          return fetchTranslations(locale, retryCount + 1);
        }

        // Échec définitif après 3 tentatives
        console.warn(
          `Échec définitif du fetch pour ${locale} (${processType}), utilisation des traductions locales`,
        );

        // Marquer comme erreur
        setErrorLocales((prev) => new Set(prev).add(locale));

        // Retirer du loading
        setLoadingLocales((prev) => {
          const newSet = new Set(prev);
          newSet.delete(locale);
          return newSet;
        });

        return null;
      }
    },
    [processType, campaignLoading, labLoading, remoteTranslations],
  );

  // Helper function to extract a translation from a translations object
  const extractTranslationFromSource = React.useCallback(
    (
      source: TranslationsAPIResponse | TTranslation | undefined,
      key: string,
    ) => {
      if (!source || !key) return null;

      const keyPaths = key.split(".");
      let _translation: unknown = source;

      for (let i = 0; _translation && i < keyPaths.length; ++i) {
        _translation = (_translation as Record<string, unknown>)[keyPaths[i]];
      }

      return _translation || null;
    },
    [],
  );

  // Helper function to process the template text with params and plural
  const processTranslationText = React.useCallback(
    (
      translationValue: TTranslationValue | string[],
      params: Record<string, string> | string[] | Record<string, unknown>,
      plural: boolean,
    ) => {
      if (!translationValue) return "";

      let templateText = "";

      // Manage the array format (our local format) vs possibly different from the backend
      if (Array.isArray(translationValue)) {
        templateText = translationValue[plural ? 1 : 0] || "";
      } else {
        // If the backend returns a different format, to adapt
        templateText = String(translationValue);
      }

      let text = "";
      let currentParam = 0;

      for (let i = 0; i < templateText.length; i++) {
        const currentChar = templateText.charAt(i);

        if (Array.isArray(params)) {
          if (
            currentChar === "%" &&
            ["d", "s"].indexOf(templateText.charAt(i + 1)) !== -1
          ) {
            text += currentParam < params.length ? params[currentParam++] : "";
            ++i;
            continue;
          }
        } else if (params) {
          if (currentChar === "$") {
            const nextChar = templateText.charAt(i + 1);

            if (nextChar === "{") {
              let extractName = "";
              let extractChar;

              for (
                let extractNameIndex = i + 2;
                extractNameIndex < templateText.length &&
                (extractChar = templateText.charAt(extractNameIndex)) !== "}";
                ++extractNameIndex
              ) {
                extractName += extractChar;
              }

              if (extractChar === "}" && Array.isArray(params) === false) {
                i += extractName.length + 2;
                text += String(
                  (params as Record<string, unknown>)[extractName] || "",
                );
                continue;
              }
            }
          }
        }

        text += currentChar;
      }

      return text;
    },
    [],
  );

  const getTranslation = React.useCallback<TGetTranslationCallback>(
    (key, { params = {}, plural = false } = {}) => {
      if (!key) return "";

      let translationValue = null;

      // 1️⃣ Priority 1 : Search in remote translations (backend)
      if (remoteTranslations[locale]) {
        translationValue = extractTranslationFromSource(
          remoteTranslations[locale],
          key,
        );
        if (translationValue) {
          return processTranslationText(
            translationValue as TTranslationValue | string[],
            params,
            plural,
          );
        }
      }

      // 2️⃣ Priority 2 : Search in local files (current locale)
      if (translations.current[locale]) {
        translationValue = extractTranslationFromSource(
          translations.current[locale],
          key,
        );
        if (translationValue) {
          return processTranslationText(
            translationValue as TTranslationValue | string[],
            params,
            plural,
          );
        }
      }

      // 3️⃣ Priority 3 : Fallback on FR if locale is different
      if (locale !== ELocale.frFr && translations.current[ELocale.frFr]) {
        translationValue = extractTranslationFromSource(
          translations.current[ELocale.frFr],
          key,
        );
        if (translationValue) {
          return processTranslationText(
            translationValue as TTranslationValue | string[],
            params,
            plural,
          );
        }
      }

      // 4️⃣ Last option : return the key or empty
      console.warn(`Translation manquante pour la clé "${key}"`);
      return renderKeyIfNotFound ? key : "";
    },
    [
      locale,
      renderKeyIfNotFound,
      remoteTranslations,
      extractTranslationFromSource,
      processTranslationText,
    ],
  );

  const getFormConfig = React.useCallback((): TFormConfig => {
    return (remoteTranslations[locale]?.form ?? {}) as TFormConfig;
  }, [locale, remoteTranslations]);

  const localeContext = React.useMemo<TLocaleContext>(
    () => ({
      locale,

      nextLocale,

      setLocale: (newLocale) => {
        Cookie.set(localeCookieName, newLocale);
        // Trigger the fetch of translations for the new locale
        fetchTranslations(newLocale as ELocale);

        if (getTranslationFromRemote) {
          dispatch({
            type: ELocaleActionType.SetRemote,
            locale: newLocale as ELocale,
          });
        } else {
          dispatch({
            type: ELocaleActionType.SetLocal,
            locale: newLocale as ELocale,
          });
        }
      },

      getTranslation,

      getFormConfig,
    }),
    [
      locale,
      nextLocale,
      getTranslation,
      getFormConfig,
      getTranslationFromRemote,
      fetchTranslations,
    ],
  );

  // Function to change the locale automatically
  const changeLocaleAutomatically = React.useCallback(() => {
    const newLanguage = detectLanguage();

    // Change only if the detected language is different from the current locale
    if (newLanguage !== locale) {
      // Trigger the fetch of translations for the new locale
      fetchTranslations(newLanguage);

      if (getTranslationFromRemote) {
        dispatch({
          type: ELocaleActionType.SetRemote,
          locale: newLanguage,
        });
      } else {
        dispatch({
          type: ELocaleActionType.SetLocal,
          locale: newLanguage,
        });
      }
    }
  }, [locale, fetchTranslations, getTranslationFromRemote]);

  // Configure the JWT token when it becomes available
  React.useEffect(() => {
    if (availableToken) {
      API.setAuthToken!(availableToken);
    }
  }, [availableToken]);

  React.useEffect(() => {
    fetchTranslations(locale);
  }, [locale, fetchTranslations]);

  // Keep document lang in sync with current locale (accessibility, SEO)
  React.useEffect(() => {
    document.documentElement.setAttribute("lang", locale);
  }, [locale]);

  /* The locale can change through the header switcher, the browser language
   * detection or the svr_it forcing below: notifying from a single effect on
   * `locale` covers all of them. Automatic detection does not write the locale
   * cookie, hence carrying the value in the event rather than letting the
   * listener read it back. */
  useDidUpdate(function notifyLocaleChange() {
    dispatchLocaleChange(locale);
  }, [locale]);

  // svr_it : forcer la locale italienne (sélecteur limité à it) pour rester en it après navigation / campagne
  React.useEffect(() => {
    if (labIdentifier === "svr_it" && locale !== ELocale.itIt) {
      Cookie.set(localeCookieName, ELocale.itIt);
      fetchTranslations(ELocale.itIt);
      dispatch({ type: ELocaleActionType.SetLocal, locale: ELocale.itIt });
    }
  }, [labIdentifier, locale, fetchTranslations]);

  // Listen to changes of the lang tag and the browser
  React.useEffect(() => {
    // MutationObserver to detect changes of the lang tag
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "lang"
        ) {
          changeLocaleAutomatically();
        }
      });
    });

    // Observe the changes of attributes on the html element
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"],
    });

    // Listener for the changes of language of the browser
    const handleLanguageChange = () => {
      changeLocaleAutomatically();
    };

    // Listen to the changes of language of the browser
    window.addEventListener("languagechange", handleLanguageChange);

    // Cleanup
    return () => {
      observer.disconnect();
      window.removeEventListener("languagechange", handleLanguageChange);
    };
  }, [changeLocaleAutomatically]);

  React.useEffect(() => {
    if (injectLocaleIntoAPIOptions) {
      API.setLocale!(nextLocale as string, injectLocaleIntoAPIOptions);
    }
    if (getTranslationFromRemote) {
      if (translations.current[nextLocale]) {
        dispatch({ type: ELocaleActionType.UpdateRemote });
      } else {
        getTranslationFromRemote(nextLocale).then((translation) => {
          translations.current[nextLocale] = translation;
          dispatch({ type: ELocaleActionType.UpdateRemote });
        });
      }
    }
  }, [getTranslationFromRemote, nextLocale, injectLocaleIntoAPIOptions]);

  return loading ? (
    renderLoading
  ) : (
    <LocaleContext.Provider value={localeContext}>
      {children}
    </LocaleContext.Provider>
  );
};

export default LocaleProvider;
